import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSidenav } from '@angular/material/sidenav';
import {
  BrandSettingsCopyRequest,
  FlyoutDataType,
  FlyoutOpeningData,
  KpiSettingsResponse,
  ManageTagRequest,
  PersonDetails,
  SessionActionType,
  SessionDetails,
  SessionHistory,
} from './flyout-models';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { TriggerbeeApi } from '../core/triggerbee/triggerbee-api.enum';
import { HttpRequestDecorator } from '../core/triggerbee/http.request.decorator';
import { TargetSite } from '../core/target-site';
import { filter, share } from 'rxjs/operators';
import FlyoutHelpers from './flyout-helpers';
import { LanguageService } from '../core/language.service';
import { Logger, LoggerFactory } from '../core/logger.service';
import {
  EditResponseListFlyoutResponse,
  FilterCampaignFlyoutResponse,
  TagFlyoutResponse,
  UpgradeAccountFlyoutResponse,
  WidgetSimpleInfo,
} from '../campaign/widgets.model';
import { AudienceFlyoutResponse } from '../audience/components/audience.model';
import { WidgetSessionConditionsFlyoutResponse } from '../audience/components/session-condition-group.model';
import { NavigationEnd, Router } from '@angular/router';
import { AccountCustomFont, AccountVariable } from 'app/account-settings/account-settings-models';
import { DataPlatformVisitRow } from '../data-platform/data-platform.model';

/** Their pages own the URL (date range, open visit), so closing them leaves it alone. */
const URL_OWNING_PAGE_FLYOUTS: readonly FlyoutDataType[] = [
  FlyoutDataType.DataPlatformVisit,
  FlyoutDataType.DataPlatformProfile,
  FlyoutDataType.DataPlatformContact,
  FlyoutDataType.AudienceHub,
];

@Injectable({
  providedIn: 'root',
})
export class FlyoutService {
  private readonly logger: Logger;
  public personDetailsBS = new BehaviorSubject<PersonDetails>(undefined);
  public sessionDetailsBS = new BehaviorSubject<SessionDetails>(undefined);
  private flyoutData: FlyoutOpeningData;
  private widgetsList: Array<WidgetSimpleInfo>;
  public sidenav: MatSidenav;
  public flyoutOpeningDataBS = new BehaviorSubject<FlyoutOpeningData>(undefined);
  public flyoutUpdatedAudienceBS = new BehaviorSubject<AudienceFlyoutResponse>(undefined);
  public flyoutUpdatedReferralBS = new BehaviorSubject<any>(undefined);
  /** A profile was deleted or unmerged. The Profiles list has its own DataPlatformService, so refreshBS cannot reach it. */
  public dataPlatformProfilesChangedBS = new BehaviorSubject<number>(undefined);
  /** A visit as the flyout re-read it. The opening data is cloned, so the list holding the older
   *  copy needs the values handed back. */
  public dataPlatformVisitRefreshedBS = new Subject<DataPlatformVisitRow>();
  public flyoutWidgetSessionConditionsBS = new BehaviorSubject<WidgetSessionConditionsFlyoutResponse>(undefined);
  public flyoutUpdatedTagsBS = new BehaviorSubject<TagFlyoutResponse>(undefined);
  public flyoutResponseListBS = new BehaviorSubject<EditResponseListFlyoutResponse>(undefined);
  public flyoutUpgradedAccountBS = new BehaviorSubject<UpgradeAccountFlyoutResponse>(undefined);
  public flyoutFormSettingsBS = new BehaviorSubject<any>(undefined);
  /** Deliberately not a BehaviorSubject: the editor's left sidebar subscribes in ngOnInit and is
   *  re-created whenever the left panel switches away from Layout and back, so a replayed
   *  'submit-btn'/'back-btn' would silently add a second button. */
  public flyoutFormAddCompBS = new Subject<string | { value: boolean; widgetId: number }>();
  /** Not a BehaviorSubject: a replay would apply the recommendations again on the next visit. */
  public readonly siteCheckApplyRequested = new Subject<void>();
  public flyoutActionAddBS = new BehaviorSubject<any>(undefined);
  public flyoutHandlerSettingBS = new BehaviorSubject<any>(undefined);
  public flyoutFilterCampaignBS = new BehaviorSubject<FilterCampaignFlyoutResponse>(undefined);
  public flyoutWidgetSelectorBS = new BehaviorSubject<number[]>(undefined);
  public flyoutKpiSettingsBS = new BehaviorSubject<KpiSettingsResponse>(undefined);
  public flyoutUpdatedBrandSettings = new BehaviorSubject<{
    customFont: AccountCustomFont;
    isEdit: boolean;
  }>(undefined);

  private readonly destroyRef = inject(DestroyRef);
  private flyoutOpener: HTMLElement | null = null;

  constructor(
    public readonly loggerFactory: LoggerFactory,
    private http: HttpRequestDecorator,
    private readonly languageService: LanguageService,
    private router: Router,
  ) {
    this.logger = loggerFactory.get('FlyoutService');
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.flyoutData) {
          this.flyoutOpeningDataBS.next(undefined);
          this.personDetailsBS.next(undefined);
          this.sessionDetailsBS.next(undefined);
          this.flyoutUpdatedAudienceBS.next(undefined);
          this.flyoutUpdatedReferralBS.next(undefined);
          this.flyoutUpdatedTagsBS.next(undefined);
          this.flyoutKpiSettingsBS.next(undefined);
        }
        this.sidenav?.close();
      });
  }

  get flyoutFormSettings() {
    return this.flyoutFormSettingsBS.value;
  }

  public setSidenav(sidenav: MatSidenav) {
    this.sidenav = sidenav;
    sidenav.openedChange
      .pipe(
        filter((opened) => !opened),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.returnFocusToOpener());
  }

  public open(flyoutData: FlyoutOpeningData) {
    this.logger.debug('Opening flyout with data: ' + JSON.stringify(flyoutData));
    this.flyoutOpeningDataBS.next(JSON.parse(JSON.stringify(flyoutData)));
    this.flyoutData = flyoutData;
    if (flyoutData && flyoutData.type === FlyoutDataType.Contact) {
      this.sessionDetailsBS.next(undefined);
    } else if (flyoutData && flyoutData.type === FlyoutDataType.Session) {
      this.personDetailsBS.next(undefined);
    }
    if (!this.sidenav.opened && document.activeElement instanceof HTMLElement) {
      this.flyoutOpener = document.activeElement;
    }
    this.scrollFlyoutToTop();
    return this.sidenav.open();
  }

  /** The open flyout makes the page inert, so the opener loses focus; Material only hands it back from inside the flyout. */
  private returnFocusToOpener() {
    if (document.activeElement === document.body && this.flyoutOpener?.isConnected) {
      this.flyoutOpener.focus();
    }
    this.flyoutOpener = null;
  }

  /** A flyout often replaces another (visit/profile navigation); the new content starts at the top. */
  private scrollFlyoutToTop() {
    document.querySelector('.sidenav .mat-drawer-inner-container')?.scrollTo({ top: 0 });
  }

  public close() {
    this.logger.debug('Closing flyout');
    this.flyoutOpeningDataBS.next(undefined);
    this.personDetailsBS.next(undefined);
    this.sessionDetailsBS.next(undefined);
    this.flyoutUpdatedAudienceBS.next(undefined);
    this.flyoutUpgradedAccountBS.next(undefined);
    this.flyoutResponseListBS.next(undefined);
    // this.flyoutFilterCampaignBS.next(undefined);
    this.flyoutWidgetSelectorBS.next(undefined);
    this.dataPlatformProfilesChangedBS.next(undefined);
    // Several pages share this subject with different metric catalogs, so a selection left behind
    // would replay into the next page that subscribes and be taken for an edit there.
    this.flyoutKpiSettingsBS.next(undefined);

    if (
      this.flyoutData.type !== FlyoutDataType.CampaignFilter &&
      this.flyoutData.type !== FlyoutDataType.WidgetPreview &&
      this.flyoutData.type !== FlyoutDataType.WidgetSelectorFilter &&
      this.flyoutData.type !== FlyoutDataType.HoldoutSettings &&
      this.flyoutData.type !== FlyoutDataType.KpiSettings &&
      this.flyoutData.type !== FlyoutDataType.Recommendation &&
      this.flyoutData.type !== FlyoutDataType.SiteCheckRecommendations &&
      !URL_OWNING_PAGE_FLYOUTS.includes(this.flyoutData.type)
    ) {
      // sessionid goes too, so a refresh does not reopen the flyout the deep link opened.
      this.router.navigate([], {
        queryParams: { from: null, sessionid: null },
        queryParamsHandling: 'merge',
      });
    }
    return this.sidenav.close();
  }

  public applySiteCheckRecommendations(): void {
    this.siteCheckApplyRequested.next();
    this.close();
  }

  public closeEditResponseListFlyout(data: EditResponseListFlyoutResponse) {
    this.sendDataEditResponseListFlyout(data);
    this.close();
  }

  public closeWidgetSelectorFlyout(selectedIds: number[]) {
    this.flyoutWidgetSelectorBS.next(selectedIds);
    this.close();
  }

  public closeFilterCampaignFlyout(data: FilterCampaignFlyoutResponse) {
    //this.sendFilterCampaignFlyout(data);
    this.close();
  }

  public sendFilterCampaignFlyout(data: FilterCampaignFlyoutResponse) {
    this.flyoutFilterCampaignBS.next(data);
  }

  public sendDataEditResponseListFlyout(data: EditResponseListFlyoutResponse) {
    this.flyoutResponseListBS.next(data);
  }

  public closeAudienceFlyout(data: AudienceFlyoutResponse) {
    this.flyoutUpdatedAudienceBS.next(data);
    this.close();
  }

  public closeBrandSettinsFlyout(data: { customFont: AccountCustomFont; isEdit: boolean }) {
    this.flyoutUpdatedBrandSettings.next(data);
    this.close();
  }

  public closeWidgetSessionConditionsFlyout(data: WidgetSessionConditionsFlyoutResponse) {
    this.flyoutWidgetSessionConditionsBS.next(data);
    this.close();
  }

  public closeUpgradeAccountFlyout(data: UpgradeAccountFlyoutResponse) {
    this.flyoutUpgradedAccountBS.next(data);
    this.close();
  }

  public closeReferralFlyout(data: any) {
    this.flyoutUpdatedReferralBS.next(data);
    this.close();
  }

  public closeTagsFlyout(data: TagFlyoutResponse) {
    this.flyoutUpdatedTagsBS.next(data);
    this.close();
  }

  public setWidgetsList(widgets: Array<WidgetSimpleInfo>) {
    this.widgetsList = widgets;
  }

  public getWidgetsList(): Array<WidgetSimpleInfo> {
    return this.widgetsList;
  }

  public loadPersonDetails(identifier: string, uid?: number): Observable<PersonDetails> {
    const request$ = this.onOpenedSite(() => this.getPersonDetails(identifier, uid)).pipe(share());
    request$.subscribe((personDetails: PersonDetails) => {
      if (personDetails) {
        personDetails = FlyoutHelpers.parseCalculatedPropertiesOnPerson(personDetails);
        personDetails.ocEvents = FlyoutHelpers.parseOnsiteCampaignActivity(
          personDetails.widgets,
          this.flyoutData.widgets,
          this.languageService,
        );
        this.personDetailsBS.next(personDetails);
      } else {
        this.logger.warn(
          `Could not get profile for identifier: ${identifier}, profile probably deleted or not created yet`,
        );
      }
    });
    return request$;
  }

  public emptyPersonDetails() {
    this.personDetailsBS.next(undefined);
  }

  public loadSessionDetails(
    sessionId: number,
    widgetIdToCampaignIdMap: { number: number },
    campaignIdToCampaignNameMap: { number: string },
  ): Observable<SessionDetails> {
    const request$ = this.onOpenedSite(() => this.getSessionDetails(sessionId)).pipe(share());
    request$.subscribe((sessionDetails: SessionDetails) => {
      if (sessionDetails && sessionDetails.actionsList) {
        sessionDetails.actionsList = sessionDetails.actionsList.filter(
          (a) => a.type !== SessionActionType.WidgetDismissal && a.title !== SessionActionType.PartialSubmit,
        );
        sessionDetails.actionsList.sort((a, b) => (a.time > b.time ? 1 : -1));
      }
      sessionDetails = FlyoutHelpers.parseCalculatedPropertiesOnSession(
        sessionDetails,
        widgetIdToCampaignIdMap,
        campaignIdToCampaignNameMap,
        this.languageService,
      );
      sessionDetails.ocEvents = FlyoutHelpers.parseOnsiteCampaignActivity(
        sessionDetails.widgets,
        this.flyoutData.widgets,
        this.languageService,
      );
      this.sessionDetailsBS.next(sessionDetails);
    });
    return request$;
  }

  public emptySessionDetails() {
    this.sessionDetailsBS.next(undefined);
  }

  private getPersonDetails(identifier: string, uid?: number): Observable<PersonDetails> {
    const body = { identifier: identifier ?? '', uid: uid ? Number(uid) : 0 };
    return this.http.post(this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/details/person', body);
  }

  public getSessionDetails(sessionId: number): Observable<SessionDetails | any> {
    const params = {
      sessionId: sessionId,
    };

    return this.http.get(this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/details/visit', params);
  }

  public addTagToVisitObject(request: ManageTagRequest, tag: string): Observable<PersonDetails> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/details/tags/${tag}`;
    return this.onOpenedSite(() => this.http.post(url, request));
  }

  public removeTagFromVisitObject(request: ManageTagRequest, tag: string): Observable<PersonDetails> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/details/tags/${tag}/remove`;
    return this.onOpenedSite(() => this.http.post(url, request));
  }

  /** The gateway resolves visit details on the target account; read the emission, as the flyout loads before `open` sets `flyoutData`. */
  private onOpenedSite<T>(build: () => T): T {
    const siteId = this.flyoutOpeningDataBS.value?.siteId;
    return siteId ? TargetSite.withSiteSync(siteId, build) : build();
  }

  public async getAutomationActionHistory(
    automationId: string,
    onlyFailed: boolean,
    identifier: string,
    skip: number,
    take: number,
  ): Promise<SessionHistory[]> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    const body = { identifier, onlyFailed, skip, take };
    const response = await this.http.post(`${baseUrl}/automations/actions/actions-history/${automationId}`, body);
    return response.toPromise();
  }

  copyBrandSettings(
    shouldCopyDesign: boolean,
    shouldCopyTextVariables: boolean,
    shouldReplaceTextVariables: boolean,
    siteIdsToCopyTo: number[],
  ): Observable<any> {
    const request: BrandSettingsCopyRequest = {
      siteIdsToCopyTo: siteIdsToCopyTo,
      shouldCopyColors: shouldCopyDesign,
      shouldCopyFonts: shouldCopyDesign,
      shouldCopyBrandVariables: shouldCopyDesign,
      shouldCopyImages: false,
      shouldCopyTexts: shouldCopyTextVariables,
      shouldReplaceTextVariables: shouldReplaceTextVariables,
    };

    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/brand-settings/copy';
    return this.http.post(url, request);
  }

  public get flyoutHandlerSetting() {
    return this.flyoutHandlerSettingBS.value;
  }
}
